import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import WorkCard from "@/components/WorkCard";
import WorkModal from "@/components/WorkModal";
import { WORK_ITEMS } from "@/data/work";

const HEADLINE = ["WE FORGE", "DIGITAL", "WEAPONS."];

const CHAPTERS = [
  { n: "01", title: "Zero Templates", desc: "Every pixel and every line of code is bespoke. Your brand gets a machine built for it alone — never a reskinned theme." },
  { n: "02", title: "Precision Engineering", desc: "Sub-second loads, fluid 60fps motion, bulletproof responsive architecture. Performance is the aesthetic." },
  { n: "03", title: "Conversion Obsession", desc: "Beautiful is table stakes. We engineer funnels disguised as art — sites that sell while they stun." },
  { n: "04", title: "Relentless Craft", desc: "Direct access to the people building your site. No account managers, no telephone games, no compromises." },
];

function MaskedLine({ text, index }) {
  return (
    <div className="overflow-hidden py-1">
      <motion.div
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay: 0.25 + index * 0.14, ease: [0.16, 1, 0.3, 1] }}
      >
        {text}
      </motion.div>
    </div>
  );
}

export default function Home() {
  const [active, setActive] = useState(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [12, -12]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-16, 16]);

  return (
    <main data-testid="home-page">
      <Seo
        title="Fredline Studios — Websites Forged, Not Templated"
        siteName="Fredline Studios"
        description="Fredline Studios builds bespoke, high-performance websites for brands that refuse templates. View our work and start a project."
        image="/images/fredline-mark.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Fredline Studios",
          url: "https://fredlinestudios.com",
          description: "Bespoke web design and engineering studio.",
        }}
      />

      <section
        data-testid="hero-section"
        className="hero-spotlight relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pt-28 pb-16 lg:px-10"
        onMouseMove={(e) => {
          mx.set(e.clientX / window.innerWidth - 0.5);
          my.set(e.clientY / window.innerHeight - 0.5);
        }}
      >
        <div className="grid-lines absolute inset-0" />
        <div className="pointer-events-none absolute left-6 top-28 hidden font-mono text-[10px] tracking-[0.3em] text-slate-600 lg:block" style={{ writingMode: "vertical-rl" }}>
          FREDLINE.STUDIO / 01
        </div>

        <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="mono-label mb-6 flex items-center gap-3"
            >
              <span className="inline-block h-px w-10 bg-cyan-400" />
              FREDLINE STUDIOS — WEB DESIGN & ENGINEERING
            </motion.p>

            <h1 className="font-display metal-text text-[15vw] font-black leading-[0.92] tracking-tighter sm:text-[11vw] lg:text-[6.2rem] xl:text-[7rem]">
              {HEADLINE.map((line, i) => (
                <MaskedLine key={line} text={line} index={i} />
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 max-w-md text-base leading-relaxed text-slate-400"
            >
              A studio for brands that refuse templates. We design and engineer bespoke websites that out-perform, out-convert, and out-last.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/work"
                data-testid="hero-view-work-button"
                className="group inline-flex items-center gap-2 bg-cyan-400 px-7 py-4 font-mono text-[12px] font-bold tracking-[0.2em] text-[#07080C] uppercase transition-all duration-200 hover:bg-cyan-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)] active:scale-[0.97]"
              >
                View the Work
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-start-project-button"
                className="inline-flex items-center gap-2 border border-[#2A3045] px-7 py-4 font-mono text-[12px] font-bold tracking-[0.2em] text-slate-200 uppercase transition-colors duration-200 hover:border-cyan-400/60 hover:text-cyan-300 active:scale-[0.97]"
              >
                Start a Project
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto flex items-center justify-center"
            style={{ perspective: 1200 }}
          >
            <div className="logo-halo absolute h-[130%] w-[130%]" />
            <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}>
              <motion.img
                src="/images/fredline-mark.png"
                alt="Fredline Studios metallic F logo"
                data-testid="hero-logo-centerpiece"
                className="relative w-64 sm:w-80 lg:w-[26rem] drop-shadow-[0_0_70px_rgba(0,240,255,0.35)]"
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
            <span className="absolute -bottom-2 right-4 font-mono text-[10px] tracking-[0.3em] text-slate-600">MARK — TITANIUM/AZURE</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600"
        >
          <span className="font-mono text-[10px] tracking-[0.3em]">SCROLL</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </motion.div>
      </section>

      <Marquee />

      <section data-testid="manifesto-section" className="relative px-6 py-28 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <span className="mono-label">THE MANIFESTO</span>
            <h2 className="font-display mt-4 max-w-2xl text-3xl font-extrabold tracking-tight text-slate-100 sm:text-5xl">
              Four laws every build obeys.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px border border-[#1E2335] bg-[#1E2335] sm:grid-cols-2">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08} className="bg-[#0D0E15]">
                <div
                  data-testid={`manifesto-chapter-${c.n}`}
                  className="group h-full p-8 transition-colors duration-300 hover:bg-[#12141F] sm:p-10"
                >
                  <span className="font-mono text-sm tracking-[0.3em] text-slate-600 transition-colors duration-300 group-hover:text-cyan-400">
                    /{c.n}
                  </span>
                  <h3 className="font-display mt-5 text-2xl font-bold tracking-tight text-slate-100">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="work-teaser-section" className="relative px-6 pb-28 lg:px-10">
        <div className="mx-auto max-w-7xl">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <span className="mono-label">SELECTED CONCEPTS</span>
              <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-100 sm:text-5xl">
                Fresh from the forge.
              </h2>
            </div>
            <Link
              to="/work"
              data-testid="work-teaser-view-all-link"
              className="group inline-flex items-center gap-2 font-mono text-[12px] font-bold tracking-[0.2em] text-cyan-300 uppercase"
            >
              Full Archive
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-14 grid gap-8 lg:grid-cols-3 lg:gap-6">
            {WORK_ITEMS.map((item, i) => (
              <div key={item.id} className={i === 1 ? "lg:mt-14" : ""}>
                <WorkCard item={item} index={i} onOpen={setActive} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-cta-section" className="relative overflow-hidden border-t border-[#1E2335] px-6 py-32 text-center lg:px-10">
        <div className="hero-spotlight absolute inset-0" />
        <Reveal className="relative z-10">
          <span className="mono-label">NEXT BUILD</span>
          <h2 className="font-display metal-text mx-auto mt-6 max-w-4xl text-4xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            YOUR COMPETITORS ALREADY LOOK ORDINARY.
          </h2>
          <Link
            to="/contact"
            data-testid="home-cta-button"
            className="mt-12 inline-flex items-center gap-2 bg-cyan-400 px-9 py-5 font-mono text-[13px] font-bold tracking-[0.2em] text-[#07080C] uppercase transition-all duration-200 hover:bg-cyan-300 hover:shadow-[0_0_50px_rgba(0,240,255,0.5)] active:scale-[0.97]"
          >
            Start Your Project
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>

      <WorkModal item={active} onClose={() => setActive(null)} />
    </main>
  );
}
